/**
 * TaskCreateModal Component
 *
 * Modal di creazione di un nuovo task Kanban Planner, sui servizi tipizzati
 * (useCreateKanbanTask/RequestLookupSelect/UserMultiSelect). `requestId` blocca e nasconde
 * la selezione richiesta (compatibilità con RequestDetail.tsx/RequestTasksSection.tsx);
 * `defaultRequestId` la prevalorizza restando modificabile (usato dalla Kanban).
 */
import { useEffect } from 'react';
import { useForm } from '@mantine/form';
import { Button, Group, Modal, Select, Stack, TextInput, Textarea, Title } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconCheck } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

import { useCreateKanbanTask } from '../../hooks/use-kanban-tasks';
import { useKanbanColumns } from '../../hooks/use-kanban-columns';
import { toDateOnlyString } from '../../utils/date-utils';
import RequestLookupSelect from '../kanban/RequestLookupSelect';
import UserMultiSelect from '../kanban/UserMultiSelect';

interface TaskCreateModalProps {
  opened: boolean;
  onClose: () => void;
  onTaskCreated?: (taskId: number) => void;
  requestId?: string | number;
  defaultRequestId?: number;
  defaultColumnId?: number;
}

interface CreateTaskFormValues {
  title: string;
  description: string;
  priorityString: string;
  requestId: number | undefined;
  assigneeIds: number[];
  dueDate: Date | null;
  kanbanColumnId: string;
}

const PRIORITY_OPTIONS = [
  { value: 'Low', label: 'Bassa' },
  { value: 'Medium', label: 'Media' },
  { value: 'High', label: 'Alta' },
  { value: 'Critical', label: 'Critica' },
];

export default function TaskCreateModal({
  opened,
  onClose,
  onTaskCreated,
  requestId,
  defaultRequestId,
  defaultColumnId,
}: TaskCreateModalProps) {
  const lockedRequestId = requestId != null ? Number(requestId) : undefined;
  const createTask = useCreateKanbanTask();
  const { data: columns = [] } = useKanbanColumns();

  const initialValues = (): CreateTaskFormValues => ({
    title: '',
    description: '',
    priorityString: 'Medium',
    requestId: lockedRequestId ?? defaultRequestId,
    assigneeIds: [],
    dueDate: null,
    kanbanColumnId: defaultColumnId != null ? String(defaultColumnId) : '',
  });

  const form = useForm<CreateTaskFormValues>({
    initialValues: initialValues(),
    validate: {
      title: (value) => {
        if (!value || value.trim().length < 3) return 'Il titolo deve contenere almeno 3 caratteri';
        if (value.trim().length > 200) return 'Il titolo non può superare 200 caratteri';
        return null;
      },
      description: (value) => (value && value.length > 1000 ? 'La descrizione non può superare 1000 caratteri' : null),
      requestId: (value) => (!lockedRequestId && !value ? 'Seleziona una richiesta' : null),
    },
  });

  useEffect(() => {
    if (opened) {
      form.setValues(initialValues());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, lockedRequestId, defaultRequestId, defaultColumnId]);

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = form.onSubmit((values) => {
    createTask.mutate(
      {
        title: values.title.trim(),
        description: values.description?.trim() || undefined,
        requestId: (lockedRequestId ?? values.requestId)!,
        priorityString: values.priorityString,
        dueDate: values.dueDate ? toDateOnlyString(values.dueDate) : undefined,
        assigneeIds: values.assigneeIds.length ? values.assigneeIds : undefined,
        kanbanColumnId: values.kanbanColumnId ? Number(values.kanbanColumnId) : undefined,
      },
      {
        onSuccess: (task) => {
          notifications.show({ color: 'green', title: 'Task creato!', message: 'Il nuovo task è stato aggiunto' });
          onTaskCreated?.(task.id);
          form.reset();
          onClose();
        },
        onError: () => {
          notifications.show({ color: 'red', message: 'Impossibile creare il task' });
        },
      }
    );
  });

  const columnOptions = [
    { value: '', label: 'Automatica in base al tipo di richiesta' },
    ...columns.filter((c) => c.isActive).map((c) => ({ value: String(c.id), label: c.name })),
  ];

  return (
    <Modal opened={opened} onClose={handleClose} title={<Title order={4}>Nuovo Task</Title>} size="lg" centered>
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <TextInput label="Titolo" placeholder="Titolo del task" required {...form.getInputProps('title')} />

          <Textarea
            label="Descrizione"
            placeholder="Descrizione del task (opzionale)"
            minRows={3}
            maxRows={6}
            {...form.getInputProps('description')}
          />

          <Group grow>
            <Select label="Priorità" data={PRIORITY_OPTIONS} {...form.getInputProps('priorityString')} />
            <DatePickerInput
              label="Data prevista"
              placeholder="Seleziona una data"
              locale="it"
              valueFormat="DD/MM/YYYY"
              clearable
              {...form.getInputProps('dueDate')}
            />
          </Group>

          {!lockedRequestId && (
            <RequestLookupSelect
              value={form.values.requestId}
              onChange={(id) => form.setFieldValue('requestId', id)}
              withTasksOnly={false}
              label="Richiesta"
              required
              placeholder="Seleziona una richiesta"
            />
          )}

          <UserMultiSelect
            value={form.values.assigneeIds}
            onChange={(ids) => form.setFieldValue('assigneeIds', ids)}
            label="Assegnatari"
          />

          <Select
            label="Colonna"
            description="Se non selezionata, il task viene inserito automaticamente in base al tipo di richiesta"
            data={columnOptions}
            {...form.getInputProps('kanbanColumnId')}
          />

          <Group justify="flex-end">
            <Button variant="subtle" onClick={handleClose} disabled={createTask.isPending}>
              Annulla
            </Button>
            <Button type="submit" loading={createTask.isPending} leftSection={<IconCheck size={16} />}>
              Crea Task
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
